import { useEffect, useRef, useState } from "react";
import {
    Plus, Play, Square, Zap, CircleDot, Trash2, GitBranch, Save, Rocket,
    Pencil, Link2, X, History, Workflow as WorkflowIcon,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";

const NODE_META = {
    start: { icon: Play, color: "#06D6A0", label: "Start" },
    activity: { icon: CircleDot, color: "#00B4D8", label: "Activity" },
    auto: { icon: Zap, color: "#B8860B", label: "Auto activity" },
    stop: { icon: Square, color: "#EF476F", label: "Stop" },
};
const ROLES = ["planner", "admin", "reviewer", "developer", "staff", "system", "public"];
const NODE_W = 168;
const NODE_H = 54;

function edgePath(a, b) {
    const x1 = a.x + NODE_W, y1 = a.y + NODE_H / 2;
    const x2 = b.x, y2 = b.y + NODE_H / 2;
    const dx = Math.max(40, Math.abs(x2 - x1) / 2);
    return `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
}

export default function WorkflowManager() {
    const { tr } = useLanguage();
    const [workflows, setWorkflows] = useState([]);
    const [mappings, setMappings] = useState([]);
    const [selected, setSelected] = useState(null); // workflow version being viewed/edited
    const [draft, setDraft] = useState(null);       // editable copy
    const [selNode, setSelNode] = useState(null);
    const [selEdge, setSelEdge] = useState(null);
    const [connectFrom, setConnectFrom] = useState(null);
    const [newWf, setNewWf] = useState({ name: "", key: "" });
    const [showNew, setShowNew] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [fitView, setFitView] = useState(false);
    const canvasRef = useRef(null);
    const dragRef = useRef(null);

    const load = async () => {
        try {
            const [w, m] = await Promise.all([api.get("/workflows"), api.get("/workflows/mappings/list")]);
            setWorkflows(w.data);
            setMappings(m.data);
            return w.data;
        } catch (e) { toast.error(formatApiError(e)); return []; }
    };
    useEffect(() => { load().then((ws) => { if (ws.length && !selected) openVersion(ws[0]); }); }, []);

    const openVersion = (wf) => {
        setSelected(wf);
        setDraft(JSON.parse(JSON.stringify(wf)));
        setSelNode(null); setSelEdge(null); setConnectFrom(null); setDirty(false);
    };

    const byKey = workflows.reduce((acc, w) => { (acc[w.key] = acc[w.key] || []).push(w); return acc; }, {});

    const mutate = (fn) => {
        if (draft?.status !== "draft") return;
        setDraft((d) => { const c = JSON.parse(JSON.stringify(d)); fn(c); return c; });
        setDirty(true);
    };

    // ---- node drag (pointer events on canvas) ----
    const onNodePointerDown = (e, node) => {
        if (draft?.status !== "draft") return;
        if (connectFrom) { finishConnect(node); return; }
        e.preventDefault();
        setSelNode(node.id); setSelEdge(null);
        const rect = canvasRef.current.getBoundingClientRect();
        dragRef.current = { id: node.id, dx: e.clientX - rect.left - node.x, dy: e.clientY - rect.top - node.y };
    };
    const onCanvasPointerMove = (e) => {
        const drag = dragRef.current;
        if (!drag) return;
        const rect = canvasRef.current.getBoundingClientRect();
        const x = Math.max(0, e.clientX - rect.left - drag.dx);
        const y = Math.max(0, e.clientY - rect.top - drag.dy);
        mutate((c) => { const n = c.nodes.find((n) => n.id === drag.id); if (n) { n.x = Math.round(x); n.y = Math.round(y); } });
    };
    const onCanvasPointerUp = () => { dragRef.current = null; };

    // ---- connect mode ----
    const startConnect = (nodeId) => { setConnectFrom(nodeId); setSelNode(null); };
    const finishConnect = (target) => {
        if (!connectFrom || connectFrom === target.id) { setConnectFrom(null); return; }
        mutate((c) => c.edges.push({ id: `e-${connectFrom}-${target.id}-${Date.now() % 100000}`, from: connectFrom, to: target.id, rule: "", parallel: false }));
        setConnectFrom(null);
        toast.success(tr("Activity linked"));
    };

    const addNode = (type) => {
        if (["start", "stop"].includes(type) && draft.nodes.some((n) => n.type === type)) {
            toast.error(tr("Only one Start/Stop node allowed")); return;
        }
        mutate((c) => c.nodes.push({
            id: `n${Date.now() % 1000000}`, type,
            label: type === "activity" ? "New activity" : type === "auto" ? "Auto activity" : NODE_META[type].label,
            role: type === "activity" ? "planner" : type === "auto" ? "system" : "",
            x: 60 + (c.nodes.length % 5) * 190, y: 40 + (c.nodes.length % 3) * 90,
        }));
    };

    const deleteNode = (id) => mutate((c) => {
        c.nodes = c.nodes.filter((n) => n.id !== id);
        c.edges = c.edges.filter((e) => e.from !== id && e.to !== id);
    });
    const deleteEdge = (id) => mutate((c) => { c.edges = c.edges.filter((e) => e.id !== id); });

    // ---- persistence ----
    const save = async () => {
        try {
            const { data } = await api.put(`/workflows/${draft.id}`, { nodes: draft.nodes, edges: draft.edges, name: draft.name, description: draft.description });
            setDraft(data); setDirty(false);
            toast.success(tr("Draft saved"));
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const publish = async () => {
        try {
            if (dirty) await api.put(`/workflows/${draft.id}`, { nodes: draft.nodes, edges: draft.edges, name: draft.name, description: draft.description });
            const { data } = await api.post(`/workflows/${draft.id}/publish`);
            toast.success(`${tr("Published")} — v${data.version} ${tr("is now live")}`);
            setDirty(false);
            const ws = await load();
            const fresh = ws.find((w) => w.id === data.id);
            if (fresh) openVersion(fresh);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const newDraftFrom = async (key) => {
        try {
            const { data } = await api.post(`/workflows/${key}/draft`);
            toast.success(tr("Draft created from live version"));
            await load(); openVersion(data);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const discardDraft = async () => {
        try {
            await api.delete(`/workflows/${draft.id}`);
            toast.success(tr("Draft discarded"));
            const ws = await load();
            const pub = ws.find((w) => w.key === selected.key && w.status === "published");
            if (pub) openVersion(pub); else setSelected(null);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const createWorkflow = async () => {
        if (!newWf.name.trim() || !newWf.key.trim()) { toast.error(tr("Name and key required")); return; }
        try {
            const { data } = await api.post("/workflows", newWf);
            toast.success(tr("Workflow created"));
            setShowNew(false); setNewWf({ name: "", key: "" });
            await load(); openVersion(data);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const saveMapping = async (idx, key) => {
        const prev = mappings;
        const next = mappings.map((m, i) => (i === idx ? { ...m, workflow_key: key } : m));
        setMappings(next);
        try { await api.put("/workflows/mappings", { mappings: next }); toast.success(tr("Mapping saved")); }
        catch (e) { setMappings(prev); toast.error(formatApiError(e)); }
    };

    const nodeById = (id) => draft?.nodes.find((n) => n.id === id);
    const editing = draft?.status === "draft";
    const selNodeObj = selNode ? nodeById(selNode) : null;
    const selEdgeObj = selEdge ? draft?.edges.find((e) => e.id === selEdge) : null;

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6" data-testid="workflow-manager">
            {/* ── left: workflow list + mapping ── */}
            <div className="space-y-4">
                <div className="bg-card rounded-[16px] border border-slate-200/60 dark:border-white/10 card-shadow p-4">
                    <div className="flex items-center justify-between mb-3">
                        <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-slate-400">{tr("Workflows")}</div>
                        <button data-testid="wf-new-btn" onClick={() => setShowNew(true)}
                            className="inline-flex items-center gap-1 text-[11px] font-bold text-[#0092B0] hover:text-[#00B4D8]">
                            <Plus className="w-3.5 h-3.5" /> {tr("New")}
                        </button>
                    </div>
                    <div className="space-y-2.5">
                        {Object.entries(byKey).map(([key, versions]) => {
                            const pub = versions.find((v) => v.status === "published");
                            const drf = versions.find((v) => v.status === "draft");
                            const active = selected && selected.key === key;
                            return (
                                <div key={key} className={`rounded-[12px] border p-3 transition-colors ${active ? "border-[#00B4D8] bg-[#00B4D8]/5" : "border-slate-200 dark:border-white/10"}`}
                                    data-testid={`wf-item-${key}`}>
                                    <button className="w-full text-left" onClick={() => openVersion(drf || pub || versions[0])} data-testid={`wf-open-${key}`}>
                                        <div className="text-sm font-bold text-navy dark:text-slate-100">{versions[0].name}</div>
                                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                                            {pub && <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#06D6A0]/12 text-[#05A37E]">v{pub.version} {tr("live")}</span>}
                                            {drf && <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#FFB703]/15 text-[#B8860B]">v{drf.version} {tr("draft")}</span>}
                                            <span className="text-[10px] text-slate-400 font-data">{key}</span>
                                        </div>
                                    </button>
                                    <div className="flex gap-2 mt-2">
                                        {!drf && pub && (
                                            <button data-testid={`wf-newdraft-${key}`} onClick={() => newDraftFrom(key)}
                                                className="inline-flex items-center gap-1 text-[10px] font-bold text-[#0092B0] hover:underline">
                                                <Pencil className="w-3 h-3" /> {tr("Edit (new draft)")}
                                            </button>
                                        )}
                                        {versions.filter((v) => v.status === "archived").map((v) => (
                                            <button key={v.id} data-testid={`wf-restore-${v.id.slice(0, 8)}`} title={`${tr("Restore as new draft")} — v${v.version}`}
                                                onClick={async () => {
                                                    try {
                                                        const { data } = await api.post(`/workflows/${v.id}/restore`);
                                                        toast.success(`v${v.version} → ${tr("restored as draft")} v${data.version}`);
                                                        await load(); openVersion(data);
                                                    } catch (e) { toast.error(formatApiError(e)); }
                                                }}
                                                className="inline-flex items-center gap-0.5 text-[10px] font-data font-bold text-slate-400 hover:text-[#0092B0] border border-slate-200 dark:border-white/10 rounded px-1.5 py-0.5 transition-colors">
                                                <History className="w-3 h-3" /> v{v.version}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* functionality ↔ workflow mapping */}
                <div className="bg-card rounded-[16px] border border-slate-200/60 dark:border-white/10 card-shadow p-4" data-testid="wf-mapping">
                    <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-slate-400 mb-1">{tr("Functionality → Workflow mapping")}</div>
                    <p className="text-[10px] text-slate-400 mb-3">{tr("Which workflow drives each part of the application.")}</p>
                    <div className="space-y-2.5">
                        {mappings.map((m, i) => (
                            <div key={i} data-testid={`wf-mapping-row-${i}`}>
                                <div className="text-[11px] font-semibold text-navy dark:text-slate-200 leading-tight mb-1">{m.functionality}</div>
                                <select value={m.workflow_key} onChange={(e) => saveMapping(i, e.target.value)}
                                    data-testid={`wf-mapping-select-${i}`}
                                    className="w-full rounded-[8px] border border-slate-200 dark:border-white/10 bg-background px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#00B4D8]">
                                    {Object.keys(byKey).map((k) => <option key={k} value={k}>{`${byKey[k][0].name} (${k})`}</option>)}
                                </select>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── right: editor canvas ── */}
            <div className="space-y-4 min-w-0">
                {!draft ? (
                    <div className="text-sm text-slate-400 py-16 text-center">{tr("Select a workflow to view or edit.")}</div>
                ) : (
                    <>
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="min-w-0">
                                <div className="flex items-center gap-2.5">
                                    <WorkflowIcon className="w-4 h-4 text-[#00B4D8]" />
                                    <span className="font-heading font-bold text-lg text-navy dark:text-slate-100 truncate" data-testid="wf-canvas-title">{draft.name}</span>
                                    <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${draft.status === "published" ? "bg-[#06D6A0]/12 text-[#05A37E]" : draft.status === "draft" ? "bg-[#FFB703]/15 text-[#B8860B]" : "bg-slate-100 text-slate-500"}`} data-testid="wf-status-pill">
                                        v{draft.version} · {tr(draft.status)}
                                    </span>
                                </div>
                                <p className="text-xs text-slate-400 mt-0.5 truncate">{draft.description}</p>
                            </div>
                            <div className="flex gap-2">
                                {editing && (
                                    <>
                                        <button data-testid="wf-save-btn" onClick={save} disabled={!dirty}
                                            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-xs font-bold border border-slate-300 dark:border-white/15 hover:border-[#00B4D8] transition-colors disabled:opacity-40">
                                            <Save className="w-3.5 h-3.5" /> {tr("Save draft")}
                                        </button>
                                        <button data-testid="wf-publish-btn" onClick={publish}
                                            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-xs font-bold text-white bg-[#06D6A0] hover:bg-[#05c394] transition-colors">
                                            <Rocket className="w-3.5 h-3.5" /> {tr("Publish (go live)")}
                                        </button>
                                        <button data-testid="wf-discard-btn" onClick={discardDraft}
                                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[8px] text-xs font-bold text-red-500 border border-red-200 hover:bg-red-50 transition-colors">
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </>
                                )}
                                {!editing && draft.status === "published" && (
                                    <button data-testid="wf-makedraft-btn" onClick={() => newDraftFrom(draft.key)}
                                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-xs font-bold text-white bg-[#00B4D8] hover:bg-[#0092B0] transition-colors">
                                        <Pencil className="w-3.5 h-3.5" /> {tr("Edit (creates a draft)")}
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* palette */}
                        {editing && (
                            <div className="flex items-center gap-2 flex-wrap" data-testid="wf-palette">
                                <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-400">{tr("Add")}:</span>
                                {["start", "activity", "auto", "stop"].map((t) => {
                                    const M = NODE_META[t];
                                    return (
                                        <button key={t} data-testid={`wf-add-${t}`} onClick={() => addNode(t)}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold border transition-colors hover:-translate-y-0.5 duration-200"
                                            style={{ borderColor: `${M.color}55`, color: M.color, backgroundColor: `${M.color}0d` }}>
                                            <M.icon className="w-3 h-3" /> {tr(M.label)}
                                        </button>
                                    );
                                })}
                                <span className="text-[10px] text-slate-400 ml-2">{tr("Drag nodes to arrange · click a node, then “Link to…” to connect · click an arrow to edit its rule")}</span>
                            </div>
                        )}

                        {/* canvas */}
                        <div className="relative">
                        <div ref={canvasRef}
                            data-testid="wf-canvas"
                            onPointerMove={onCanvasPointerMove}
                            onPointerUp={onCanvasPointerUp}
                            onPointerLeave={onCanvasPointerUp}
                            className={`relative rounded-[16px] border border-slate-200 dark:border-white/10 bg-[#F8FAFC] dark:bg-white/[0.03] overflow-auto select-none ${connectFrom ? "cursor-crosshair" : ""}`}
                            style={{ height: 420, backgroundImage: "radial-gradient(circle, #CBD5E1 1px, transparent 1px)", backgroundSize: "22px 22px" }}>
                            <div style={fitView ? {
                                transform: `scale(${Math.min(1, (canvasRef.current?.clientWidth - 24 || 1200) / (Math.max(...draft.nodes.map((n) => n.x + NODE_W)) + 60))})`,
                                transformOrigin: "0 0",
                            } : undefined}>
                            <svg className="absolute inset-0 pointer-events-none" style={{ width: "100%", height: "100%", minWidth: 1400 }}>
                                <defs>
                                    <marker id="wf-arrow" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
                                        <path d="M0,0 L8,4 L0,8 z" fill="#64748B" />
                                    </marker>
                                </defs>
                                {draft.edges.map((e) => {
                                    const a = nodeById(e.from), b = nodeById(e.to);
                                    if (!a || !b) return null;
                                    const isSel = selEdge === e.id;
                                    return (
                                        <g key={e.id} className="pointer-events-auto cursor-pointer" onClick={() => { setSelEdge(e.id); setSelNode(null); }} data-testid={`wf-edge-${e.id}`}>
                                            <path d={edgePath(a, b)} fill="none" stroke={isSel ? "#00B4D8" : e.parallel ? "#B8860B" : "#64748B"}
                                                strokeWidth={isSel ? 3 : 2} strokeDasharray={e.parallel ? "6 4" : "none"} markerEnd="url(#wf-arrow)" />
                                            {e.rule && (
                                                <text x={(a.x + NODE_W + b.x) / 2} y={(a.y + b.y) / 2 + NODE_H / 2 - 8}
                                                    textAnchor="middle" fontSize="10" fontWeight="700" fill={isSel ? "#0092B0" : "#64748B"}>{e.rule}</text>
                                            )}
                                        </g>
                                    );
                                })}
                            </svg>
                            {draft.nodes.map((n) => {
                                const M = NODE_META[n.type] || NODE_META.activity;
                                const isSel = selNode === n.id;
                                const isConnectSrc = connectFrom === n.id;
                                return (
                                    <div key={n.id}
                                        data-testid={`wf-node-${n.id}`}
                                        title={n.label}
                                        onPointerDown={(e) => onNodePointerDown(e, n)}
                                        className={`absolute rounded-[12px] border-2 bg-white dark:bg-[#0f2237] shadow-sm px-3 py-2 transition-shadow ${editing ? "cursor-grab active:cursor-grabbing" : ""} ${isSel ? "ring-2 ring-[#00B4D8] shadow-lg" : ""} ${isConnectSrc ? "ring-2 ring-[#B8860B] animate-pulse" : ""}`}
                                        style={{ left: n.x, top: n.y, width: NODE_W, borderColor: M.color }}>
                                        <div className="flex items-center gap-1.5">
                                            <M.icon className="w-3.5 h-3.5 shrink-0" style={{ color: M.color }} />
                                            <span className="text-[11px] font-bold text-navy dark:text-slate-100 leading-tight truncate">{n.label}</span>
                                        </div>
                                        <div className="flex items-center justify-between mt-1">
                                            <span className="text-[9px] font-data text-slate-400 uppercase tracking-wide">{n.role ? tr(n.role) : tr(NODE_META[n.type]?.label || n.type)}</span>
                                            {editing && !["start", "stop"].includes(n.type) && (
                                                <button data-testid={`wf-link-${n.id}`} onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); startConnect(n.id); }}
                                                    title={tr("Link to…")}
                                                    className="p-0.5 rounded text-slate-300 hover:text-[#B8860B] transition-colors">
                                                    <Link2 className="w-3 h-3" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            {connectFrom && (
                                <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-[#B8860B] text-white text-[11px] font-bold px-4 py-1.5 rounded-full shadow-lg flex items-center gap-2" data-testid="wf-connect-banner">
                                    {tr("Click a target activity to connect")} <button onClick={() => setConnectFrom(null)}><X className="w-3.5 h-3.5" /></button>
                                </div>
                            )}
                            </div>
                        </div>
                        <button data-testid="wf-fit-btn" onClick={() => setFitView((f) => !f)}
                            className={`absolute bottom-3 right-3 text-[10px] font-bold px-2.5 py-1.5 rounded-[8px] border shadow-sm transition-colors ${fitView ? "bg-[#0A2540] text-white border-[#0A2540]" : "bg-white dark:bg-[#0f2237] text-slate-500 border-slate-200 dark:border-white/10 hover:border-[#00B4D8]"}`}>
                            {fitView ? tr("Scroll view") : tr("Fit to view")}
                        </button>
                        </div>

                        {/* inspector: node */}
                        {editing && selNodeObj && (
                            <div className="bg-card rounded-[14px] border border-slate-200/60 dark:border-white/10 card-shadow p-4" data-testid="wf-node-inspector">
                                <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-slate-400 mb-3">{tr("Activity settings")} — {tr(NODE_META[selNodeObj.type].label)}</div>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <div>
                                        <label className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{tr("Label")}</label>
                                        <input data-testid="wf-node-label" value={selNodeObj.label}
                                            onChange={(e) => mutate((c) => { c.nodes.find((n) => n.id === selNode).label = e.target.value; })}
                                            className="mt-1 w-full rounded-[8px] border border-slate-200 dark:border-white/10 bg-background px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00B4D8]" />
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{tr("Responsible role")}</label>
                                        <select data-testid="wf-node-role" value={selNodeObj.role || ""}
                                            onChange={(e) => mutate((c) => { c.nodes.find((n) => n.id === selNode).role = e.target.value; })}
                                            className="mt-1 w-full rounded-[8px] border border-slate-200 dark:border-white/10 bg-background px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00B4D8]">
                                            <option value="">{tr("— none —")}</option>
                                            {ROLES.map((r) => <option key={r} value={r}>{tr(r)}</option>)}
                                        </select>
                                    </div>
                                    {draft.key === "circulation_review" && (
                                        <div>
                                            <label className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{tr("Status id (Kanban column)")}</label>
                                            <input data-testid="wf-node-statusid" value={selNodeObj.status_id || ""}
                                                onChange={(e) => mutate((c) => { c.nodes.find((n) => n.id === selNode).status_id = e.target.value; })}
                                                className="mt-1 w-full rounded-[8px] border border-slate-200 dark:border-white/10 bg-background px-2.5 py-2 text-sm font-data focus:outline-none focus:ring-2 focus:ring-[#00B4D8]" />
                                        </div>
                                    )}
                                </div>
                                {!["start", "stop"].includes(selNodeObj.type) && (
                                    <button data-testid="wf-node-delete" onClick={() => { deleteNode(selNode); setSelNode(null); }}
                                        className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-bold text-red-500 hover:underline">
                                        <Trash2 className="w-3.5 h-3.5" /> {tr("Delete activity")}
                                    </button>
                                )}
                            </div>
                        )}

                        {/* inspector: edge */}
                        {editing && selEdgeObj && (
                            <div className="bg-card rounded-[14px] border border-slate-200/60 dark:border-white/10 card-shadow p-4" data-testid="wf-edge-inspector">
                                <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-slate-400 mb-3">
                                    {tr("Transition")} — {nodeById(selEdgeObj.from)?.label} → {nodeById(selEdgeObj.to)?.label}
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
                                    <div>
                                        <label className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{tr("Transition rule (shown on the arrow — e.g. “when fees paid”)")}</label>
                                        <input data-testid="wf-edge-rule" value={selEdgeObj.rule || ""}
                                            onChange={(e) => mutate((c) => { c.edges.find((x) => x.id === selEdge).rule = e.target.value; })}
                                            placeholder={tr("optional condition…")}
                                            className="mt-1 w-full rounded-[8px] border border-slate-200 dark:border-white/10 bg-background px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00B4D8]" />
                                    </div>
                                    <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-slate-300 pb-2">
                                        <input type="checkbox" data-testid="wf-edge-parallel" checked={!!selEdgeObj.parallel}
                                            onChange={(e) => mutate((c) => { c.edges.find((x) => x.id === selEdge).parallel = e.target.checked; })}
                                            className="accent-[#B8860B]" />
                                        {tr("Parallel branch")}
                                    </label>
                                </div>
                                <div className="flex items-center gap-4 mt-3">
                                    <span className="text-[10px] text-slate-400 inline-flex items-center gap-1"><GitBranch className="w-3 h-3" /> {tr("Multiple outgoing arrows from one activity = parallel flow; single chain = serial flow.")}</span>
                                    <button data-testid="wf-edge-delete" onClick={() => { deleteEdge(selEdge); setSelEdge(null); }}
                                        className="inline-flex items-center gap-1.5 text-[11px] font-bold text-red-500 hover:underline">
                                        <Trash2 className="w-3.5 h-3.5" /> {tr("Delete transition")}
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* new workflow dialog */}
            {showNew && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-[#0A2540]/55 backdrop-blur-sm" onClick={() => setShowNew(false)} />
                    <div className="relative bg-white dark:bg-[#0f2237] rounded-[18px] shadow-2xl w-full max-w-md p-6" data-testid="wf-new-dialog">
                        <h3 className="font-heading font-bold text-lg text-navy dark:text-slate-100">{tr("New workflow")}</h3>
                        <div className="mt-4 space-y-3">
                            <input data-testid="wf-new-name" value={newWf.name} onChange={(e) => setNewWf((f) => ({ ...f, name: e.target.value }))} placeholder={tr("Name (e.g. Site Plan Fast Track)")}
                                className="w-full rounded-[8px] border border-slate-200 dark:border-white/10 bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#00B4D8]" />
                            <input data-testid="wf-new-key" value={newWf.key} onChange={(e) => setNewWf((f) => ({ ...f, key: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "_") }))} placeholder={tr("key_snake_case")}
                                className="w-full rounded-[8px] border border-slate-200 dark:border-white/10 bg-background px-3 py-2.5 text-sm font-data focus:outline-none focus:ring-2 focus:ring-[#00B4D8]" />
                        </div>
                        <div className="mt-5 flex justify-end gap-2">
                            <button onClick={() => setShowNew(false)} className="px-4 py-2 rounded-[8px] text-sm font-bold border border-slate-300 dark:border-white/15">{tr("Cancel")}</button>
                            <button data-testid="wf-new-create" onClick={createWorkflow} className="px-4 py-2 rounded-[8px] text-sm font-bold text-white bg-[#00B4D8] hover:bg-[#0092B0]">{tr("Create draft")}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
